import { motion } from "framer-motion";
import { ArrowRight, Compass, Zap, Shield, Lightbulb, TrendingUp, Clock, Search, Layers, Settings, BarChart3, ShieldCheck, Network, Bot, BrainCircuit, Eye, FileText, Monitor } from "lucide-react";
import type { LucideIcon } from "lucide-react";

const ICON_REGISTRY: Record<string, LucideIcon> = {
  Compass, Zap, Shield, Lightbulb, TrendingUp, Clock, Search, Layers, Settings, BarChart3,
  ShieldCheck, Network, Bot, BrainCircuit, Eye, FileText, Monitor,
};
import { Button } from "@/components/ui/button";
import { Section, SectionHeader } from "@/components/solutions/shared";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";
import type { SolutionData } from "@/data/solutionsData";

interface Props {
  solution: SolutionData;
  onCtaClick: () => void;
}

const RichText = ({ text }: { text: string }) => (
  <p
    className="text-muted-foreground leading-relaxed"
    dangerouslySetInnerHTML={{
      __html: text.replace(
        /\*\*(.*?)\*\*/g,
        '<strong class="text-foreground font-semibold">$1</strong>'
      ),
    }}
  />
);

const ChallengeItem = ({
  c,
  i,
  compact,
}: {
  c: SolutionData["challenges"][0];
  i: number;
  compact?: boolean;
}) => (
  <motion.div
    key={i}
    className={compact ? "flex items-start gap-3" : "flex items-start gap-3"}
    initial={{ opacity: 0, x: -8 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.3, delay: i * 0.06 }}
  >
    {c.icon && (
      <c.icon className="text-primary shrink-0 mt-0.5" size={18} strokeWidth={1.5} />
    )}
    <div>
      <span className="text-sm font-semibold text-foreground">{c.title}</span>
      <span className="text-sm text-muted-foreground"> — {c.desc}</span>
    </div>
  </motion.div>
);

const SUBSECTION_COLORS = ["#0E7C6B", "#1B4F8A", "#6B4C9A", "#C6701E"];

const SubsectionCards = ({ subsections, examplesLabel }: { subsections: NonNullable<SolutionData["solutionApproach"]["subsections"]>; examplesLabel: string }) => (
  <div className="grid sm:grid-cols-2 gap-6 mt-10">
    {subsections.map((sub, i) => {
      const accentColor = SUBSECTION_COLORS[i % SUBSECTION_COLORS.length];
      return (
        <motion.div
          key={i}
          className="rounded-2xl border border-border/60 bg-card overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: i * 0.12 }}
        >
          <div className="h-1.5" style={{ backgroundColor: accentColor }} />

          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${accentColor}15` }}
              >
                <span className="text-lg font-bold" style={{ color: accentColor }}>
                  {i + 1}
                </span>
              </div>
              <h3 className="text-base font-bold text-foreground leading-tight">{sub.title}</h3>
            </div>

            <p
              className="text-sm text-muted-foreground leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: sub.content.replace(
                  /\*\*(.*?)\*\*/g,
                  '<strong class="text-foreground font-semibold">$1</strong>'
                ),
              }}
            />

            {sub.tags && sub.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {sub.tags.map((tag, ti) => (
                  <span
                    key={ti}
                    className="inline-flex items-center text-xs px-3 py-1.5 rounded-lg font-medium border"
                    style={{
                      backgroundColor: `${accentColor}08`,
                      borderColor: `${accentColor}25`,
                      color: accentColor,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {sub.examples && sub.examples.length > 0 && (
              <div className="border-t border-border/50 pt-4 mt-4 space-y-2">
                <p className="text-[11px] font-bold text-muted-foreground/70 uppercase tracking-[0.1em]">
                  {examplesLabel}
                </p>
                {sub.examples.map((ex, ei) => (
                  <div key={ei} className="flex items-start gap-2">
                    <span className="text-primary mt-1 shrink-0">→</span>
                    <p className="text-xs text-muted-foreground leading-relaxed">{ex}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      );
    })}
  </div>
);

const FALLBACK_ICONS = [Compass, Zap, Shield, Lightbulb, TrendingUp, Clock, Search, Layers, Settings, BarChart3];

const getUniqueIcons = (challenges: SolutionData["challenges"]) => {
  const usedIcons = new Set(challenges.filter(c => c.icon).map(c => c.icon));
  const available = FALLBACK_ICONS.filter(ic => !usedIcons.has(ic));
  let idx = 0;
  return challenges.map(c => {
    if (c.icon) return c.icon;
    return available[idx++ % available.length] || Compass;
  });
};

const BulletCardsGrid = ({ cards }: { cards: NonNullable<SolutionData["solutionApproach"]["bulletCards"]> }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
    {cards.map((card, i) => {
      const Icon = ICON_REGISTRY[card.iconName] || Compass;
      return (
        <motion.div
          key={i}
          className="relative rounded-xl border border-border bg-card p-6 hover:border-primary/40 hover:shadow-md transition-all overflow-hidden"
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.35, delay: i * 0.07 }}
        >
          <span className="absolute top-0 left-0 w-full h-1 bg-primary" />
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
            <Icon size={24} className="text-primary" strokeWidth={1.6} />
          </div>
          <h3 className="text-sm font-semibold text-foreground leading-snug">{card.title}</h3>
        </motion.div>
      );
    })}
  </div>
);

const SplitLayout = ({ solution, challengesLabel }: { solution: SolutionData; challengesLabel: string }) => {
  const challengeIcons = getUniqueIcons(solution.challenges);
  const showChallenges = solution.challenges.length > 0 && !solution.challengesSection;
  const bullets = solution.solutionApproach.bullets;
  const bulletCards = solution.solutionApproach.bulletCards;
  const useCards = bulletCards && bulletCards.length > 0;

  return (
    <div>
      <div className={showChallenges && !useCards ? "grid lg:grid-cols-2 gap-12" : "max-w-3xl mx-auto"}>
        <div className="space-y-4">
          {solution.solutionApproach.content.split("\n\n").map((p, i) => (
            <RichText key={i} text={p} />
          ))}
          {!useCards && bullets && bullets.length > 0 && (
            <ul className="space-y-2.5 mt-4">
              {bullets.map((b, i) => (
                <motion.li
                  key={i}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                >
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  <span className="text-muted-foreground leading-relaxed">{b}</span>
                </motion.li>
              ))}
            </ul>
          )}
          {solution.sectionImages?.afterIntro && (
            <motion.div
              className="mt-6"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <img
                src={solution.sectionImages.afterIntro.src}
                alt={solution.sectionImages.afterIntro.alt}
                className="w-full h-auto rounded-2xl"
                loading="lazy"
              />
            </motion.div>
          )}
        </div>

        {showChallenges && !useCards && (
          <div className="space-y-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              {challengesLabel}
            </p>
            {solution.challenges.map((c, i) => {
              const Icon = challengeIcons[i];
              return (
                <motion.div
                  key={i}
                  className="rounded-xl border border-border bg-card p-5 flex items-start gap-4"
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.08 }}
                >
                  <Icon className="text-primary shrink-0 mt-0.5" size={20} strokeWidth={1.5} />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">{c.title}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">{c.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {useCards && <BulletCardsGrid cards={bulletCards!} />}
    </div>
  );
};

const SolutionApproach = ({ solution, onCtaClick }: Props) => {
  const lang = useLanguage();
  const t = uiT(lang);

  const timelineSteps = solution.solutionApproach.timelineSteps;
  const hasTimeline = timelineSteps && timelineSteps.length > 0;

  return (
    <Section>
      <div className="mb-10">
        <SectionHeader label={solution.solutionApproach.label || t("ourApproach")} title={solution.solutionApproach.title} icon={Compass} />
      </div>

      {hasTimeline ? (
        <div className="relative">
          {/* Horizontal connector line (desktop) */}
          <div className="hidden lg:block absolute top-6 left-0 right-0 h-0.5 bg-primary/20" aria-hidden />
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${timelineSteps!.length === 5 ? "lg:grid-cols-5" : timelineSteps!.length === 4 ? "lg:grid-cols-4" : timelineSteps!.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-6 lg:gap-4`}>
            {timelineSteps!.map((step, i) => (
              <motion.div
                key={i}
                className="relative flex flex-col items-start"
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.08 }}
              >
                <div className="relative z-10 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-base mb-4 shadow-md">
                  {i + 1}
                </div>
                <h3 className="text-sm font-bold text-foreground mb-2 leading-snug">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      ) : (
        <SplitLayout solution={solution} challengesLabel={t("keyChallenges")} />
      )}

      {!hasTimeline && solution.solutionApproach.subsections &&
        solution.solutionApproach.subsections.length > 0 && (
          <div>
            <SubsectionCards subsections={solution.solutionApproach.subsections} examplesLabel={t("examples")} />
          </div>
        )}

      {solution.sectionImages?.approachImage && (
        <motion.div
          className="max-w-3xl mx-auto mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <img
            src={solution.sectionImages.approachImage.src}
            alt={solution.sectionImages.approachImage.alt}
            className="w-full h-auto rounded-2xl"
            loading="lazy"
          />
        </motion.div>
      )}

      {solution.hubspotFormId && (
        <div className="text-center mt-12">
          <Button className="rounded-full px-8" onClick={onCtaClick}>
            {t("getExpertGuidance")} <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      )}
    </Section>
  );
};

export default SolutionApproach;
